import { useArtifactStorageApi, useFinanceApi, useRecruitmentApi, useDeliveryApi } from 'infrastructure/api';
import type { ArtifactsInfoResponse } from './types';

interface ArtifactInfoService {
  getThumbnails: (id: string) => Promise<Dictionary<string>>;
  getArtifactsInfo: (id: string) => Promise<ArtifactsInfoResponse>;

  getInvoiceThumbnails: (id: string) => Promise<Dictionary<string>>;
  getInvoiceArtifactsInfo: (id: string) => Promise<ArtifactsInfoResponse>;

  getCandidateThumbnails: (id: string) => Promise<Dictionary<string>>;
  getCandidateArtifactsInfo: (id: string) => Promise<ArtifactsInfoResponse>;

  getProjectThumbnails: (id: string) => Promise<Dictionary<string>>;
  getProjectArtifactsInfo: (id: string) => Promise<ArtifactsInfoResponse>;

  getRiskThumbnails: (riskId: string, id: string) => Promise<Dictionary<string>>;
  getRiskArtifactsInfo: (riskId: string, ids: string) => Promise<ArtifactsInfoResponse>;

  getStaffingThumbnails: (path: string) => Promise<Dictionary<string>>;
  getStaffingArtifactsInfo: (paths: string) => Promise<ArtifactsInfoResponse>;

  getBudgetArtifactsInfo: (budgetId: StringOrNumber, ids: string) => Promise<ArtifactsInfoResponse>;
}

const useArtifactInfoService = (): ArtifactInfoService => {
  const { sendGetRequest } = useArtifactStorageApi();
  const { sendGetRequest: sendFinanceGetRequest } = useFinanceApi();
  const { sendGetRequest: sendRecruitmentGetRequest } = useRecruitmentApi();
  const { sendGetRequest: sendDeliveryGetRequest } = useDeliveryApi();

  const getArtifactsInfo = async (id: string) => {
    const response = await sendGetRequest(`/artifacts/${id}/thumbnails`);

    return response.json();
  };

  const getInvoiceArtifactsInfo = async (id: string) => {
    const response = await sendFinanceGetRequest(`/invoices/${id}/artifacts/thumbnails`);

    return response.json();
  };

  const getCandidateArtifactsInfo = async (id: string) => {
    const response = await sendRecruitmentGetRequest(`/candidates/artifacts/${id}/thumbnails`);

    return response.json();
  };

  const getProjectArtifactsInfo = async (id: string) => {
    const response = await sendDeliveryGetRequest(`/projects/timeline/artifacts/${id}/thumbnails`);

    return response.json();
  };

  const getRiskArtifactsInfo = async (riskId: string, ids: string) => {
    const response = await sendDeliveryGetRequest(`/projects/risks/${riskId}/artifacts/${ids}/thumbnails`);

    return response.json();
  };

  const getStaffingArtifactsInfo = async (paths: string) => {
    const response = await sendDeliveryGetRequest(`/projects/staffing-notes/artifacts/${paths}/thumbnails`);

    return response.json();
  };

  const getBudgetArtifactsInfo = async (budgetId: StringOrNumber, ids: string) => {
    const response = await sendDeliveryGetRequest(`/budgets/${budgetId}/documents/${ids}/thumbnails`);

    return response.json();
  };

  const getStaffingThumbnails = async (path: string) => {
    const encodedPath = encodeURIComponent(path);
    const { artifacts = {} } = await getStaffingArtifactsInfo(encodedPath);

    return artifacts[path]?.thumbnails || artifacts[encodedPath]?.thumbnails || {};
  };

  const getThumbnails = async (id: string) => {
    const { artifacts = {} } = await getArtifactsInfo(id);

    return artifacts[id]?.thumbnails || {};
  };

  const getInvoiceThumbnails = async (id: string) => {
    const { artifacts = {} } = await getInvoiceArtifactsInfo(id);

    return artifacts[id]?.thumbnails || {};
  };

  const getCandidateThumbnails = async (id: string) => {
    const { artifacts = {} } = await getCandidateArtifactsInfo(id);

    return artifacts[id]?.thumbnails || {};
  };

  const getProjectThumbnails = async (id: string) => {
    const { artifacts = {} } = await getProjectArtifactsInfo(id);

    return artifacts[id]?.thumbnails || {};
  };

  const getRiskThumbnails = async (riskId: string, id: string) => {
    const { artifacts = {} } = await getRiskArtifactsInfo(riskId, id);

    return artifacts[id]?.thumbnails || {};
  };

  return {
    getThumbnails,
    getInvoiceThumbnails,
    getArtifactsInfo,
    getInvoiceArtifactsInfo,
    getStaffingArtifactsInfo,
    getStaffingThumbnails,
    getCandidateArtifactsInfo,
    getCandidateThumbnails,
    getProjectArtifactsInfo,
    getProjectThumbnails,
    getRiskArtifactsInfo,
    getRiskThumbnails,
    getBudgetArtifactsInfo,
  };
};

export default useArtifactInfoService;
